import {
  applyColorBrightness,
  blendColors,
  getColorFromString,
  getRandomBrightColor,
  hexToRGBA,
  interpolateColorRange,
  makeColorSchema,
  rainbowColors,
  rainbowRandomColors,
  rainbowRandomColorsInterpolate,
  rgbStrToHex,
  rgbToHex,
  rgbaStrToHex,
  transformColor
} from "./chunk-SNUDSNGS.js";
import {
  __decorateClass
} from "./chunk-EUXUH3YW.js";

// libs/core/src/lib/theme/theme-utils.ts
var pattern = /(?<=var\().*?(?=\))/;
function getCssValue(cssVarName) {
  const match = cssVarName.match(pattern);
  const value = match ? match[0] : cssVarName;
  const style = getComputedStyle(document.documentElement);
  return style.getPropertyValue(value).trim();
}

// libs/core/src/lib/theme/theme-elements.ts
var mainColorStyleElement = document.createElement("style");
var brandColorStyleElement = document.createElement("style");

// libs/core/src/lib/theme/styles/font.style.ts
import { css, unsafeCSS } from "lit";
var fontStyle = (targetName = "") => css`

    @font-face { font-family: "Inter"; font-style: normal; font-weight: 500; font-display: swap; src: url("fonts/Inter/Inter-Medium.woff2") format("woff2"); }
    @font-face { font-family: "Inter"; font-style: normal; font-weight: 600; font-display: swap; src: url("fonts/Inter/Inter-SemiBold.woff2") format("woff2"); }
    @font-face { font-family: "Inter"; font-style: normal; font-weight: 400; font-display: swap; src: url("fonts/Inter/Inter-Regular.woff2") format("woff2"); }

    ${unsafeCSS(targetName)} * {
        font-family: 'Inter', sans-serif;
        font-style: normal;
    }

`;

// libs/core/src/lib/theme/styles/body.style.ts
import { css as css2 } from "lit";
var bodyStyle = css2`
    
    html {
        background-color: var(--color-background-bg-body);
        position: fixed;
        width: 100vw;
        height: 100vh;
        overflow: hidden;
    }

    body {
        margin: 0;
        background-color: var(--color-background-bg-body);
        box-sizing: border-box;
    }

    ::view-transition-old(root),
    ::view-transition-new(root) {
        animation: none;
        mix-blend-mode: normal;
    }
    
    * {
        user-select: none;
        outline: none;
        -webkit-user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
    }

`;

// libs/core/src/lib/theme/themes/index.ts
import { BrandColors, MainColors } from "@one-inch-community/models";
var mainColorMap = {
  [MainColors.systemSync]: (targetName) => getSystemTheme(targetName),
  [MainColors.dark]: (targetName) => import("./dark.style-IHZRPSJZ.js").then((m) => m.themeDark(targetName)),
  [MainColors.darkBlue]: (targetName) => import("./dark-blue.style-NUK7A3IA.js").then((m) => m.themeDarkBlue(targetName)),
  [MainColors.light]: (targetName) => import("./light.style-5IM57SAI.js").then((m) => m.themeLight(targetName)),
  [MainColors.lightBlue]: (targetName) => import("./light-blue.style-VY6NMOV6.js").then((m) => m.themeLightBlue(targetName))
};
var brandColorMap = {
  [BrandColors.community]: () => import("./community.style-RWKB44BC.js").then((m) => m.communityStyle),
  [BrandColors.rainbow]: () => import("./rainbow.style-POOUJKCE.js").then((m) => m.rainbowStyle),
  [BrandColors.random]: () => import("./random.style-5KLAHRBN.js").then((m) => m.randomStyle()),
  [BrandColors.orange]: () => import("./orange.style-SHX2AUWW.js").then((m) => m.orangeStyle),
  [BrandColors.violet]: () => import("./violet.style-Y3WLLZZ6.js").then((m) => m.violetStyle)
};
function getSystemTheme(targetName) {
  if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
    if (window?.ethereum?.isOneInchIOSWallet) return mainColorMap[MainColors.darkBlue](targetName);
    return mainColorMap[MainColors.dark](targetName);
  }
  if (window?.ethereum?.isOneInchIOSWallet) return mainColorMap[MainColors.lightBlue](targetName);
  return mainColorMap[MainColors.light](targetName);
}

// libs/core/src/lib/theme/theme-change.ts
import { applyStyle, createAndAppendInHeaderElement } from "@one-inch-community/core/lit";
import { ReplaySubject } from "rxjs";
import { BrandColors as BrandColors2, MainColors as MainColors2 } from "@one-inch-community/models";
var currentMainColor;
var currentBrandColor;
var isEmbeddedMode = false;
var browserMetaColors = {
  [MainColors2.systemSync]: () => getBrowserMetaColor(),
  [MainColors2.light]: () => "#f1f1f1",
  [MainColors2.lightBlue]: () => "#f1f1f1",
  [MainColors2.dark]: () => "#0e0e0e",
  [MainColors2.darkBlue]: () => "#0e0e0e"
};
var mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
var changeAppTheme$ = new ReplaySubject(1);
var metaColorFilter = null;
function setEmbeddedMode() {
  isEmbeddedMode = true;
}
function getBrowserMetaColor() {
  if (mediaQuery.matches) {
    return browserMetaColors[MainColors2.dark]();
  }
  return browserMetaColors[MainColors2.light]();
}
function setBrowserMetaColorFilter(filter) {
  metaColorFilter = filter;
  setBrowserMetaColorColor(browserMetaColors[currentMainColor]());
}
function setBrowserMetaColorColor(color) {
  if (isEmbeddedMode) return;
  const themeMetaElement = document.head.querySelector("#theme-color");
  if (!themeMetaElement) {
    createAndAppendInHeaderElement("meta", (meta) => {
      meta.id = "theme-color";
      meta.name = "theme-color";
      meta.content = metaColorFilter ? metaColorFilter(color, isDarkTheme(currentMainColor)) : color;
    });
    return;
  }
  themeMetaElement.content = metaColorFilter ? metaColorFilter(color, isDarkTheme(currentMainColor)) : color;
}
async function themeChangeMainColor(mainColorName, event) {
  return await themeChange(mainColorName, currentBrandColor, event);
}
async function themeChangeBrandColor(brandColorName, event) {
  return await themeChange(currentMainColor, brandColorName, event);
}
function getThemeChange() {
  return changeAppTheme$;
}
mediaQuery.onchange = async () => {
  if (currentMainColor !== MainColors2.systemSync) return;
  await themeChange(MainColors2.systemSync, currentBrandColor);
};
function isDarkTheme(theme) {
  if (theme === MainColors2.systemSync) return mediaQuery.matches;
  return theme === MainColors2.dark || theme === MainColors2.darkBlue;
}
async function themeChange(mainColorName, brandColorName, event) {
  const changeTheme = async () => {
    const mainColor = await mainColorMap[mainColorName]();
    const brandColor = await brandColorMap[brandColorName]();
    applyStyle(mainColorStyleElement, mainColor);
    applyStyle(brandColorStyleElement, brandColor);
    setBrowserMetaColorColor(browserMetaColors[mainColorName]());
    currentMainColor = mainColorName;
    currentBrandColor = brandColorName;
    document.documentElement.setAttribute("theme", isDarkTheme(mainColorName) ? "dark" : "light");
    document.documentElement.setAttribute("brand-color", BrandColors2[brandColorName]);
    changeAppTheme$.next({ mainColor: mainColorName, brandColor: brandColorName });
  };
  if (event && "startViewTransition" in document && isDarkTheme(currentMainColor) !== isDarkTheme(mainColorName)) {
    const x = event.clientX;
    const y = event.clientY;
    const right = window.innerWidth - y;
    const bottom = window.innerHeight - x;
    const maxRadius = Math.hypot(
      Math.max(y, right),
      Math.max(x, bottom)
    ) + 100;
    document.startViewTransition(() => {
      changeTheme();
    }).ready.then(() => {
      return document.documentElement.animate(
        {
          clipPath: [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${maxRadius}px at ${x}px ${y}px)`
          ]
        },
        {
          duration: 600,
          easing: "ease-in-out",
          pseudoElement: "::view-transition-new(root)"
        }
      ).finished;
    });
    return;
  }
  await changeTheme();
}

// libs/core/src/lib/theme/styles/scrollbar.style.ts
import { css as css3, unsafeCSS as unsafeCSS2 } from "lit";
import { mobileMediaCSS } from "@one-inch-community/core/lit";
var getScrollbarStyle = (hostName, hideScrollBar) => css3`
    
    ${unsafeCSS2(hostName)} {
        overflow: auto;
        touch-action: pan-y;
        overscroll-behavior: none;
        scrollbar-color: var(--primary) transparent;
        scrollbar-width: ${unsafeCSS2(hideScrollBar ? "none" : "thin")};
        scrollbar-gutter: stable;
    }

    /* width */
    ${unsafeCSS2(hostName)}::-webkit-scrollbar {
        width: 4px;
        height: 4px;
    }

    /* Track */
    ${unsafeCSS2(hostName)}::-webkit-scrollbar-track {
        background: transparent;
    }

    /* Handle */
    ${unsafeCSS2(hostName)}::-webkit-scrollbar-thumb {
        background: var(--primary);
        border-radius: 4px;
    }

    /* Handle on hover */
    ${unsafeCSS2(hostName)}::-webkit-scrollbar-thumb:hover {
        background: var(--primary-hover);
    }
    
    ${mobileMediaCSS(css3`
        ${unsafeCSS2(hostName)}::-webkit-scrollbar {
            display: none;
        }
    `)}
    
    ${unsafeCSS2(
  hideScrollBar ? css3`
          ${unsafeCSS2(hostName)}::-webkit-scrollbar {
              display: none;
          }
    ` : ""
)}
`;
var scrollbarStyle = getScrollbarStyle(":host");

// libs/core/src/lib/theme/theme-init.ts
import { createAndApplyStyle } from "@one-inch-community/core/lit";
import { BrandColors as BrandColors3, MainColors as MainColors3 } from "@one-inch-community/models";
async function themeInit(mainColorName = MainColors3.light, brandColorName = BrandColors3.community) {
  document.head.appendChild(mainColorStyleElement);
  document.head.appendChild(brandColorStyleElement);
  createAndApplyStyle(fontStyle());
  createAndApplyStyle(bodyStyle);
  createAndApplyStyle(scrollbarStyle);
  await themeChange(mainColorName, brandColorName);
}

// libs/core/src/lib/theme/rainbow.ts
import { unsafeCSS as unsafeCSS3 } from "lit";
function getRainbowGradient() {
  return unsafeCSS3(`linear-gradient(90deg, ${unsafeCSS3(rainbowRandomColorsInterpolate.join(","))})`);
}

// libs/core/src/lib/theme/elements/theme-embedded-container.element.ts
import { html, LitElement } from "lit";
import { customElement } from "lit/decorators.js";
var ThemeEmbeddedContainerElement = class extends LitElement {
  render() {
    return html`<slot></slot>`;
  }
};
ThemeEmbeddedContainerElement.tagName = "inch-theme-embedded-container";
ThemeEmbeddedContainerElement = __decorateClass([
  customElement(ThemeEmbeddedContainerElement.tagName)
], ThemeEmbeddedContainerElement);

// libs/core/src/lib/theme/theme-controller.ts
import { BrandColors as BrandColors4, MainColors as MainColors4 } from "@one-inch-community/models";
import { SettingsController } from "@one-inch-community/core/settings";
var ThemeController = class {
  constructor() {
    this.mainColorSettings = new SettingsController(
      "main-color-name",
      MainColors4.systemSync
    );
    this.brandColorSettings = new SettingsController(
      "brand-color-name",
      BrandColors4.community
    );
  }
  getActiveMainColor() {
    return this.mainColorSettings.value ?? MainColors4.systemSync;
  }
  getActiveBrandColor() {
    return this.brandColorSettings.value ?? BrandColors4.community;
  }
  async init() {
    const mainColorName = this.getActiveMainColor();
    const brandColorName = this.getActiveBrandColor();
    await themeInit(mainColorName, brandColorName);
  }
  async onChangeTheme(mainColorName, brandColorName, event) {
    this.mainColorSettings.setValue(mainColorName);
    this.brandColorSettings.setValue(brandColorName);
    return await themeChange(mainColorName, brandColorName, event);
  }
  async onChangeMainColor(mainColorName, event) {
    const brandColorName = this.getActiveBrandColor();
    await this.onChangeTheme(mainColorName, brandColorName, event);
  }
  async onChangeBrandColor(brandColorName, event) {
    const mainColorName = this.getActiveMainColor();
    await this.onChangeTheme(mainColorName, brandColorName, event);
  }
};

// libs/core/src/lib/theme/theme-embedded-controller.ts
var ThemeEmbeddedController = class {
  async init(context) {
  }
  async onChangeTheme(mainColorName, brandColorName, event) {
    throw new Error("ThemeEmbeddedController not support onChangeTheme");
  }
  async onChangeMainColor(mainColorName, event) {
    throw new Error("ThemeEmbeddedController not support onChangeMainColor");
  }
  async onChangeBrandColor(brandColorName, event) {
    throw new Error("ThemeEmbeddedController not support onChangeBrandColor");
  }
  getActiveBrandColor() {
    throw new Error("ThemeEmbeddedController not support getActiveBrandColor");
  }
  getActiveMainColor() {
    throw new Error("ThemeEmbeddedController not support getActiveMainColor");
  }
};

// libs/core/src/lib/theme/styles/skeleton.style.ts
import { css as css4, unsafeCSS as unsafeCSS4 } from "lit";
var defaultConfig = {
  prefixName: "",
  animationDuration: "2s",
  animationDelay: "0s"
};
var skeletonStyle = (config) => {
  const _config = { ...defaultConfig, ...config };
  const className = [
    "skeleton",
    _config.prefixName
  ].filter(Boolean).join("-");
  return css4`
      .${unsafeCSS4(className)} {
          position: relative;
          overflow: hidden;
          color: transparent;
      }

      .${unsafeCSS4(className)}::after {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          z-index: 1;
          transform: translateX(-110%);
          opacity: .5;
          background-image: linear-gradient(
                  90deg,
                  var(--primary-1) 0,
                  var(--primary-5) 50%,
                  var(--primary-1) 100%
          );
          animation: shimmer ${unsafeCSS4(_config.animationDuration)} infinite;
          animation-delay: ${unsafeCSS4(_config.animationDelay)};
          content: '';
      }

      @keyframes shimmer {
          100% {
              transform: translateX(110%);
          }
      }

  `;
};
export {
  ThemeController,
  ThemeEmbeddedContainerElement,
  ThemeEmbeddedController,
  applyColorBrightness,
  blendColors,
  brandColorMap,
  fontStyle,
  getBrowserMetaColor,
  getColorFromString,
  getCssValue,
  getRainbowGradient,
  getRandomBrightColor,
  getScrollbarStyle,
  getThemeChange,
  hexToRGBA,
  interpolateColorRange,
  mainColorMap,
  makeColorSchema,
  rainbowColors,
  rainbowRandomColors,
  rainbowRandomColorsInterpolate,
  rgbStrToHex,
  rgbToHex,
  rgbaStrToHex,
  scrollbarStyle,
  setBrowserMetaColorFilter,
  setEmbeddedMode,
  skeletonStyle,
  themeChange,
  themeChangeBrandColor,
  themeChangeMainColor,
  themeInit,
  transformColor
};
//# sourceMappingURL=index.esm.js.map
